import axios from "axios";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const api = axios.create({
  baseURL: BASE_URL,
  headers: { "Content-Type": "application/json" },
  withCredentials: false,
});

// ── Attach access token to every request ─────────────────────────────────────
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("lp_access_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── Auto-refresh on 401 ───────────────────────────────────────────────────────
let isRefreshing = false;
let failedQueue = [];

function processQueue(error, token = null) {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
  failedQueue = [];
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    if (
      error.response?.status === 401 &&
      !original._retry &&
      !original.url?.includes("/auth/refresh") &&
      !original.url?.includes("/auth/login")
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            original.headers.Authorization = `Bearer ${token}`;
            return api(original);
          })
          .catch((err) => Promise.reject(err));
      }

      original._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem("lp_refresh_token");
      if (!refreshToken) {
        isRefreshing = false;
        clearTokens();
        window.location.href = "/login";
        return Promise.reject(error);
      }

      try {
        const { data } = await axios.post(`${BASE_URL}/auth/refresh`, { refreshToken });
        localStorage.setItem("lp_access_token", data.accessToken);
        localStorage.setItem("lp_refresh_token", data.refreshToken);
        api.defaults.headers.common.Authorization = `Bearer ${data.accessToken}`;
        processQueue(null, data.accessToken);
        original.headers.Authorization = `Bearer ${data.accessToken}`;
        return api(original);
      } catch (err) {
        processQueue(err, null);
        clearTokens();
        window.location.href = "/login";
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

function clearTokens() {
  localStorage.removeItem("lp_access_token");
  localStorage.removeItem("lp_refresh_token");
  localStorage.removeItem("lp_user");
}

// ── Auth ──────────────────────────────────────────────────────────────────────
export const authApi = {
  signup: (data) => api.post("/auth/signup", data),
  login: (data) => api.post("/auth/login", data),
  logout: () => api.post("/auth/logout"),
  forgotPassword: (email) => api.post("/auth/forgot-password", { email }),
  resetPassword: (token, password) => api.patch(`/auth/reset-password/${token}`, { password }),
  verifyEmail: (token) => api.get(`/auth/verify-email/${token}`),
  resendVerification: (email) => api.post("/auth/resend-verification", { email }),
  getMe: () => api.get("/auth/me"),
};

// ── Users ─────────────────────────────────────────────────────────────────────
export const usersApi = {
  getMyProfile: () => api.get("/users/me/profile"),
  updateMyProfile: (data) => api.patch("/users/me/profile", data),
  changePassword: (data) => api.patch("/users/me/password", data),
  getMySummary: () => api.get("/users/me/summary"),
  startPayoutOnboarding: (country) => api.post("/users/me/payout-account/onboarding", { country }),
  getPayoutAccountStatus: () => api.get("/users/me/payout-account/status"),
  // admin
  getAll: (params) => api.get("/users", { params }),
  getById: (id) => api.get(`/users/${id}`),
  update: (id, data) => api.patch(`/users/${id}`, data),
  suspend: (id) => api.patch(`/users/${id}/suspend`),
};

// ── Pools ─────────────────────────────────────────────────────────────────────
export const poolsApi = {
  getAll: (params) => api.get("/pools", { params }),
  getById: (id) => api.get(`/pools/${id}`),
  getMyPools: () => api.get("/pools/me"),
  join: (id) => api.post(`/pools/${id}/join`),
  getMembers: (id) => api.get(`/pools/${id}/members`),
  // admin
  create: (data) => api.post("/pools", data),
  update: (id, data) => api.patch(`/pools/${id}`, data),
  cancel: (id) => api.delete(`/pools/${id}`),
  autoDraw: (id) => api.post(`/pools/${id}/auto-draw`),
  announceOutcome: (id, data) => api.patch(`/pools/${id}/outcome`, data),
};

export const ticketRequestsApi = {
  create: (data) => api.post("/ticket-requests", data),
  getMine: () => api.get("/ticket-requests/me"),
  getAll: (params) => api.get("/ticket-requests", { params }),
  getById: (id) => api.get(`/ticket-requests/${id}`),
  update: (id, data) => api.patch(`/ticket-requests/${id}`, data),
};

// ── Tickets ───────────────────────────────────────────────────────────────────
export const ticketsApi = {
  getMyTickets: () => api.get("/tickets/me"),
  getById: (id) => api.get(`/tickets/${id}`),
  // admin
  getAll: (params) => api.get("/tickets", { params }),
  create: (formData) =>
    api.post("/tickets", formData, { headers: { "Content-Type": "multipart/form-data" } }),
  update: (id, formData) =>
    api.patch(`/tickets/${id}`, formData, { headers: { "Content-Type": "multipart/form-data" } }),
};

// ── Payments ──────────────────────────────────────────────────────────────────
export const paymentsApi = {
  getMyPayments: () => api.get("/payments/me"),
  resumeTicketRequestCheckout: (requestId) => api.post(`/payments/ticket-requests/${requestId}/checkout`),
  getCheckoutStatus: (sessionId) => api.get(`/payments/checkout-sessions/${sessionId}`),
  createSubscriptionCheckout: () => api.post("/payments/subscriptions/checkout"),
  createBillingPortal: () => api.post("/payments/subscriptions/portal"),
  getSubscriptionStatus: (sessionId) => api.get("/payments/subscriptions/status", {
    params: sessionId ? { session_id: sessionId } : undefined,
  }),
  // admin
  getAll: (params) => api.get("/payments", { params }),
  getStats: () => api.get("/payments/stats"),
};

// ── Results ───────────────────────────────────────────────────────────────────
export const resultsApi = {
  getAll: (params) => api.get("/results", { params }),
  getById: (id) => api.get(`/results/${id}`),
  // admin
  create: (data) => api.post("/results", data),
  runChecker: (id, data) => api.post(`/results/${id}/run-checker`, data),
};

// ── Winners ───────────────────────────────────────────────────────────────────
export const winnersApi = {
  getMyWinnings: () => api.get("/winners/me"),
  // admin
  getAll: (params) => api.get("/winners", { params }),
  markPaid: (id) => api.patch(`/winners/${id}/payout`),
};

// ── Admin ─────────────────────────────────────────────────────────────────────
export const adminApi = {
  getStats: () => api.get("/admin/stats"),
  getLogs: (params) => api.get("/admin/logs", { params }),
  getSettings: () => api.get("/admin/settings"),
  updateSettings: (data) => api.patch("/admin/settings", data),
};

export default api;
